// src/services/demoApi/agentFileMarker.ts
// Маркер создания файла агентом (FR-3): LLM выводит в конце ответа блок
// [CREATE_FILE name="…" ext="…"]…markdown…[/CREATE_FILE]. Здесь — парсинг
// финального текста, скрытие хвоста при стриминге и срезание в истории.
import type { DamFile } from 'mocks/dam';
import type { DamFileContent, DamTextContent } from 'mocks/damContents';
import { extractFirstTable } from './markdownBlocks';

export const CREATE_FILE_OPEN = '[CREATE_FILE';
const CREATE_FILE_CLOSE = '[/CREATE_FILE]';
const OPEN_TAG_RE = /\[CREATE_FILE\s+name="([^"]+)"\s+ext="([^"]*)"\]/;
const VALID_EXTS: Array<DamFile['ext']> = ['docx', 'xlsx', 'pdf', 'pptx'];

export interface CreateFileCommand {
  name: string;
  ext: DamFile['ext'];
  content: DamFileContent;
}

export interface ParsedAgentReply {
  command: CreateFileCommand | null;
  cleanText: string;
}

/**
 * Убирает inline-разметку Markdown из строки (FR-6.1):
 * жирный, курсив, код, ссылки, маркеры списков → чистый текст.
 */
export function sanitizeMarkdownInline(line: string): string {
  let result = line;
  // Маркеры неупорядоченных списков в начале строки: "- ", "* ", "+ " → "— "
  result = result.replace(/^[-*+] /, '— ');
  // Ссылки [текст](url) → текст
  result = result.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  // Жирный **текст** или __текст__
  result = result.replace(/\*\*([^*]+)\*\*/g, '$1');
  result = result.replace(/__([^_]+)__/g, '$1');
  // Курсив *текст* (только если * граничит с пробелом/началом/концом, не внутри слова)
  result = result.replace(/(^|\s)\*(\S[^*]*\S|\S)\*(?=\s|$)/g, '$1$2');
  // Курсив _текст_ (аналогично)
  result = result.replace(/(^|\s)_(\S[^_]*\S|\S)_(?=\s|$)/g, '$1$2');
  // Инлайн-код `код`
  result = result.replace(/`([^`]+)`/g, '$1');
  return result;
}

/** Markdown-тело блока → секции (заголовки #/##/### делят на секции).
 *  body — санитизированный плоский текст (контекст агента, txt-фолбэк),
 *  bodyMd — исходный markdown для rich-рендера генераторами документов. */
function markdownToSections(body: string): DamTextContent {
  const lines = body.trim().split('\n');
  const sections: Array<{ heading: string; body: string; bodyMd: string }> = [];
  let heading = 'Документ';
  let buffer: string[] = [];
  let rawBuffer: string[] = [];
  const flush = () => {
    const text = buffer.join('\n').trim();
    const raw = rawBuffer.join('\n').trim();
    if (text) sections.push({ heading, body: text, bodyMd: raw });
    buffer = [];
    rawBuffer = [];
  };
  lines.forEach((line) => {
    const match = /^#{1,3}\s+(.+)$/.exec(line.trim());
    if (match) {
      flush();
      heading = sanitizeMarkdownInline(match[1].trim());
    } else {
      buffer.push(sanitizeMarkdownInline(line));
      rawBuffer.push(line);
    }
  });
  flush();
  if (sections.length === 0)
    sections.push({ heading: 'Документ', body: body.trim(), bodyMd: body.trim() });
  return { kind: 'text', sections };
}

/** Разбор финального ответа агента. ВСЕГДА возвращает cleanText без маркера */
export function parseCreateFileMarker(text: string): ParsedAgentReply {
  const openIdx = text.indexOf(CREATE_FILE_OPEN);
  if (openIdx === -1) return { command: null, cleanText: text };

  const before = text.slice(0, openIdx).trimEnd();
  const closeIdx = text.indexOf(CREATE_FILE_CLOSE, openIdx);
  if (closeIdx === -1) {
    // EC-2: нет закрывающего тега — маркер срезаем, файл не создаём
    return { command: null, cleanText: before };
  }

  const after = text.slice(closeIdx + CREATE_FILE_CLOSE.length).trim();
  const cleanText = after ? `${before}\n${after}` : before;

  const block = text.slice(openIdx, closeIdx);
  const tag = OPEN_TAG_RE.exec(block);
  if (!tag || !tag[1].trim()) {
    // EC-2: нет name — команду не формируем
    return { command: null, cleanText };
  }

  const rawExt = tag[2].trim().toLowerCase();
  const ext = (VALID_EXTS as string[]).includes(rawExt) ? (rawExt as DamFile['ext']) : 'docx'; // EC-5
  const body = block.slice(tag.index + tag[0].length);

  // Для xlsx markdown-таблица из тела становится настоящей таблицей (kind: 'table');
  // без таблицы — текстовый фолбэк, как для остальных форматов.
  const table = ext === 'xlsx' ? extractFirstTable(body) : null;
  const content: DamFileContent = table
    ? { kind: 'table', headers: table.headers, rows: table.rows }
    : markdownToSections(body);

  return {
    command: { name: tag[1].trim(), ext, content },
    cleanText,
  };
}

/** Срезание маркера при рендере истории (FR-3.3) */
export function stripCreateFileMarker(text: string): string {
  return parseCreateFileMarker(text).cleanText;
}

/** Информация о файле, который начал генерироваться в стриминге */
export interface StreamingFileInfo {
  /** null — открывающий тег ещё не достримился целиком */
  name: string | null;
  ext: DamFile['ext'] | null;
}

/** Если в частичном ответе начался блок CREATE_FILE — вернуть инфо о файле */
export function detectStreamingFile(partial: string): StreamingFileInfo | null {
  const idx = partial.indexOf(CREATE_FILE_OPEN);
  if (idx === -1) return null;
  const tag = OPEN_TAG_RE.exec(partial.slice(idx));
  if (!tag || !tag[1].trim()) return { name: null, ext: null };
  const rawExt = tag[2].trim().toLowerCase();
  return {
    name: tag[1].trim(),
    ext: (VALID_EXTS as string[]).includes(rawExt) ? (rawExt as DamFile['ext']) : 'docx',
  };
}

/** При стриминге скрывает маркер и его частично напечатанный префикс в конце */
export function hideStreamingMarkerTail(partial: string): string {
  const idx = partial.indexOf(CREATE_FILE_OPEN);
  if (idx !== -1) return partial.slice(0, idx).trimEnd();
  for (let len = Math.min(CREATE_FILE_OPEN.length - 1, partial.length); len > 0; len -= 1) {
    if (partial.endsWith(CREATE_FILE_OPEN.slice(0, len))) {
      return partial.slice(0, partial.length - len).trimEnd();
    }
  }
  return partial;
}
